import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'
import { apiClient } from '@/api'
import { useAuthState } from '@/composables/useAuthState'
import { createLogger } from '@/utils/logger'
import { ROLES, type Role } from '@/constants/roles'
import * as Sentry from '@sentry/vue'

const logger = createLogger('Router')

// Extend Vue Router's RouteMeta interface
declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    requiresAuth?: boolean
    allowedRoles?: Role[]
  }
}

// Lazy load components for better performance
// All route components use dynamic imports to enable route-level code splitting
const LandingPage = () => import('@/components/LandingPage.vue')
const Login = () => import('@/views/Login.vue')
const SignUp = () => import('@/views/SignUp.vue')
const Dashboard = () => import('@/views/Dashboard.vue')

const routes = [
  {
    path: '/',
    name: 'Home',
    component: LandingPage,
    meta: { title: 'Candy Cleaning Services' },
  },
  {
    path: '/login/:userType?',
    name: 'Login',
    component: Login,
    props: (route: RouteLocationNormalized) => ({
      userType: route.params.userType || 'customer',
    }),
    meta: { title: 'Login - Candy Cleaning Services' },
  },
  {
    path: '/register',
    name: 'CustomerRegister',
    component: SignUp,
    meta: { title: 'Sign Up - Candy Cleaning Services' },
  },
  {
    path: '/auth/verify-email',
    name: 'VerifyEmail',
    component: () => import('@/views/auth/VerifyEmail.vue'),
    meta: {
      title: 'Verify Email - Candy Cleaning Services',
      requiresAuth: false,
    },
  },
  {
    path: '/auth/resend-verification',
    name: 'ResendVerification',
    component: () => import('@/views/auth/ResendVerification.vue'),
    meta: {
      title: 'Resend Verification - Candy Cleaning Services',
      requiresAuth: false,
    },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: {
      title: 'Dashboard - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [
        ROLES.CUSTOMER,
        ROLES.ADMIN_SUPER,
        ROLES.ADMIN_MANAGER,
        ROLES.SERVICE_PROVIDER,
      ],
    },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/views/Settings.vue'),
    meta: {
      title: 'Settings - Candy Cleaning Services',
      requiresAuth: true,
    },
  },
  {
    path: '/bookings',
    name: 'Bookings',
    component: () => import('@/views/Bookings.vue'),
    meta: {
      title: 'Bookings - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [ROLES.CUSTOMER, ROLES.ADMIN_SUPER, ROLES.ADMIN_MANAGER],
    },
  },
  {
    path: '/locations',
    name: 'Locations',
    component: () => import('@/views/Locations.vue'),
    meta: {
      title: 'Locations - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [
        ROLES.CUSTOMER,
        ROLES.ADMIN_SUPER,
        ROLES.ADMIN_MANAGER,
        ROLES.SERVICE_PROVIDER,
      ],
    },
  },
  {
    path: '/jobs',
    name: 'Jobs',
    component: () => import('@/views/Jobs.vue'),
    meta: {
      title: 'Jobs - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [ROLES.SERVICE_PROVIDER],
    },
  },
  {
    path: '/provider/earnings',
    name: 'MyEarnings',
    component: () => import('@/views/provider/MyEarnings.vue'),
    meta: {
      title: 'My Earnings - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [ROLES.SERVICE_PROVIDER],
    },
  },
  {
    path: '/admin/employees',
    name: 'EmployeeManagement',
    component: () => import('@/views/admin/EmployeeManagement.vue'),
    meta: {
      title: 'Employee Management - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [ROLES.ADMIN_SUPER, ROLES.ADMIN_MANAGER],
    },
  },
  {
    path: '/admin/customers',
    name: 'CustomerManagement',
    component: () => import('@/views/admin/CustomerManagement.vue'),
    meta: {
      title: 'Customer Management - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [ROLES.ADMIN_SUPER, ROLES.ADMIN_MANAGER],
    },
  },
  {
    path: '/admin/earnings-overview',
    name: 'EarningsOverview',
    component: () => import('@/views/admin/EarningsOverview.vue'),
    meta: {
      title: 'Earnings Overview - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [ROLES.ADMIN_SUPER, ROLES.ADMIN_MANAGER],
    },
  },
  {
    path: '/admin/platform-fee-settings',
    name: 'PlatformFeeSettings',
    component: () => import('@/views/admin/PlatformFeeSettings.vue'),
    meta: {
      title: 'Platform Fee Settings - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
  },
  {
    path: '/admin/transactions',
    name: 'AdminTransactions',
    component: () => import('@/views/admin/Transactions.vue'),
    meta: {
      title: 'Transactions - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [ROLES.ADMIN_SUPER, ROLES.ADMIN_MANAGER],
    },
  },
  {
    path: '/provider/payouts',
    name: 'ProviderPayouts',
    component: () => import('@/views/provider/PayoutDashboard.vue'),
    meta: {
      title: 'Payouts - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [ROLES.SERVICE_PROVIDER],
    },
  },
  {
    path: '/customer/payment-methods',
    name: 'PaymentMethods',
    component: () => import('@/views/customer/PaymentMethods.vue'),
    meta: {
      title: 'Payment Methods - Candy Cleaning Services',
      requiresAuth: true,
      allowedRoles: [ROLES.CUSTOMER],
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

// Redirect loop detection - tracks navigation history to prevent infinite loops
const navigationHistory: { path: string; timestamp: number }[] = []
const MAX_SAME_PATH_REDIRECTS = 3 // Allow max 3 redirects to same path in 5 seconds
const REDIRECT_WINDOW_MS = 5000 // 5 second window

function detectRedirectLoop(path: string): boolean {
  const now = Date.now()

  // Clean up old entries outside the time window
  const recentHistory = navigationHistory.filter(
    entry => now - entry.timestamp < REDIRECT_WINDOW_MS
  )
  navigationHistory.length = 0
  navigationHistory.push(...recentHistory)

  // Add current navigation
  navigationHistory.push({ path, timestamp: now })

  // Count redirects to this path in the time window
  const redirectCount = navigationHistory.filter(
    entry => entry.path === path
  ).length

  if (redirectCount >= MAX_SAME_PATH_REDIRECTS) {
    logger.error(
      `[RouterGuard] REDIRECT LOOP DETECTED! Path "${path}" was redirected to ${redirectCount} times in ${REDIRECT_WINDOW_MS}ms`,
      'Navigation history:',
      navigationHistory
    )
    return true
  }

  return false
}

// Navigation guard for authentication
router.beforeEach(async (to, _from, next) => {
  const authState = useAuthState()

  // Detect redirect loops FIRST before any other logic
  if (detectRedirectLoop(to.path)) {
    // Loop detected! Break it by redirecting to login with error message
    logger.error('[RouterGuard] Breaking redirect loop - sending user to login')
    authState.setError(
      'Access denied: Your account may be missing required permissions. Please contact support.'
    )
    next({ path: '/login', replace: true })
    return
  }

  // Update document title
  if (to.meta.title) {
    document.title = to.meta.title as string
  }

  // Public routes - no authentication required
  if (!to.meta.requiresAuth) {
    next()
    return
  }

  // Auth required - check if authenticated
  if (!apiClient.isAuthenticated()) {
    const shouldSetRedirect = !to.path.startsWith('/login')
    const query = shouldSetRedirect
      ? { redirect: to.fullPath, userType: to.query.userType as string }
      : {}
    next({ path: '/login', query })
    return
  }

  // Get user from authState (already hydrated from localStorage on app load)
  const user = authState.user.value
  if (!user) {
    // Token exists but no user - attempt to fetch user
    try {
      const fetchedUser = await apiClient.getCurrentUser()
      authState.setUser(fetchedUser)
    } catch (error) {
      // Token invalid, redirect to login
      authState.clearUser()
      next({ path: '/login', query: { redirect: to.fullPath } })
      return
    }
  }

  // Check role requirements
  const allowedRoles = to.meta.allowedRoles as Role[] | undefined
  if (allowedRoles && allowedRoles.length > 0) {
    // Log detailed role information for debugging
    const user = authState.user.value
    logger.log('[RouterGuard] Role check for route:', to.path)
    logger.log(
      '[RouterGuard] User roles:',
      JSON.stringify(user?.user_roles || [])
    )
    logger.log('[RouterGuard] Required roles:', JSON.stringify(allowedRoles))
    logger.log('[RouterGuard] isCustomer:', authState.isCustomer.value)
    logger.log('[RouterGuard] isAdmin:', authState.isAdmin.value)
    logger.log('[RouterGuard] isSuperAdmin:', authState.isSuperAdmin.value)
    logger.log(
      '[RouterGuard] isServiceProvider:',
      authState.isServiceProvider.value
    )

    // Defensive check: if user_roles is undefined or empty but token exists
    if (!user?.user_roles || user.user_roles.length === 0) {
      logger.warn('[RouterGuard] User has no roles despite being authenticated')
    }

    const hasRequiredRole = allowedRoles.some(role => {
      let result = false
      if (role === ROLES.CUSTOMER) {
        result = authState.isCustomer.value
      } else if (role === ROLES.ADMIN_SUPER) {
        result = authState.isSuperAdmin.value
      } else if (role === ROLES.ADMIN_MANAGER) {
        // Fix: Use hasRole() to check for exact role match
        result = authState.hasRole(ROLES.ADMIN_MANAGER)
      } else if (role === ROLES.SERVICE_PROVIDER) {
        result = authState.isServiceProvider.value
      } else {
        result = authState.hasRole(role)
      }
      logger.log(`[RouterGuard] Role '${role}' check result:`, result)
      return result
    })

    logger.log('[RouterGuard] hasRequiredRole:', hasRequiredRole)

    if (!hasRequiredRole) {
      logger.warn(
        '[RouterGuard] Access denied - insufficient permissions. User roles:',
        user?.user_roles,
        'Required roles:',
        allowedRoles
      )

      // CRITICAL FIX: Don't redirect to /dashboard (which also requires roles!)
      // Instead, redirect to login with error message
      authState.setError(
        'Access denied: You do not have permission to access this page.'
      )

      // Clear auth state if user has no roles (likely a broken session)
      if (!user?.user_roles || user.user_roles.length === 0) {
        logger.error(
          '[RouterGuard] User has no roles - clearing auth state and redirecting to login'
        )
        await authState.logout()
      }

      next({ path: '/login', replace: true })
      return
    }
  }

  next()
})

// Error handler for route component loading failures
router.onError(error => {
  Sentry.withScope(scope => {
    scope.setTag('error_type', 'router')
    scope.setTag('route', router.currentRoute.value.path)
    Sentry.captureException(error)
  })
})

export default router
